<template>
  <v-card id="mapCard" outlined>
    <v-card-text class="pr-5 local-table">
      <v-row no-gutters>
        <v-col cols="4" xl="3" class="pa-0 mb-2">
          <RealTimeSelectAreaComponent />
        </v-col>
        <v-col cols="4" xl="3" class="pa-0 mb-2">
          <v-switch
            v-model="poiView"
            dense
            hide-details="auto"
            :label="`${poiView ? 'Poi' : labels.vehicles}`"
          ></v-switch>
        </v-col>
        <v-col v-if="mobileBreakPoint" cols="4" xl="3" class="pa-0 mb-2">
            <v-btn x-small class="mb-3 mt-4"  @click="onFocusAll">{{ labels.focusAll }}</v-btn>
        </v-col>
      </v-row>

      <RealTimeLabelSwitchComponent :mobileBreakPoint="mobileBreakPoint" />
      <RealTimeVehicleStatusFilterComponent v-if="!poiView" />
      <RealTimeMapVehicleAndVehicleGroupsFilter v-if="!poiView && showVehicleGroupFilter && !tenant.showTenantDaughtersVehicles" />
      <RealTimePoiFilterComponent v-if="poiView" />
      <RealTimeSearchComponent :mobileBreakPoint="mobileBreakPoint" :poiView="poiView" />
      <v-row v-if="!mobileBreakPoint" no-gutters>
            <v-btn x-small class="mb-3 mt-4"  @click="onFocusAll">{{ labels.focusAll }}</v-btn>
      </v-row>

      <v-data-table
        v-if="tenant.readRealTimeMapAdditionalColumns"
        :fixed-header="true"
        ref="vehicleTable"
        class="text-caption"
        width="100%"
        multi-sort
        dense
        :footer-props="{ 'items-per-page-text': $t('perPage'), 'items-per-page-all-text': $t('restTable.allRecords') }"
        :height="tableHeight"
        :headers="headers"
        :items="vehicleForTable"
        mobile-breakpoint
        :options.sync="options"
      >
        <template v-slot:body="{ items }">
          <tbody>
            <template v-for="item in items">
              <tr
                v-if="item.shouldDisplay()"
                :key="item.vehicleLabel"
                :class="getColor(item)"
                @click="handleRowClick(item)"
              >
                <td>
                  <input type="checkbox" dense height="0px" v-model="item.focused" />
                </td>
                <td v-if="hasMultipleTenants">
                  {{ item.tenantName }} - {{  item.vehicleLabel }}
                </td>
                <td v-else>{{ item.vehicleLabel }}</td>
                <td>{{ item.todayKillometers }}</td>
                <td>{{ item.speed }}</td>
                <td v-if="additionalColumnExist('driverName')">{{ item.driverName }}</td>
                <td v-if="additionalColumnExist('poi')">
                  <span v-if="item.poi">{{ item.poi.name }}</span>
                </td>
                <td v-if="additionalColumnExist('poiEnteredAt')">
                  <span
                    v-if="item.poiEnteredAt"
                  >{{ convertDate(item.poiEnteredAt) }}</span>
                </td>
                <td v-if="additionalColumnExist('label')">
                  <span v-if="item.label">{{ item.label }}</span>
                </td>
                <td v-if="additionalColumnExist('engineBlockage')">
                  <template v-if="item.useEngineBlockageActivation && item.engineBlockageState !== undefined">
                    <v-btn small color="primary" @click="engineBlockage(item)" :disabled="!item.engineBlockageButtonEnabled">{{ item.engineBlockageState ? labels.engineBlockageDeactivation : labels.engineBlockageActivation }}</v-btn>
                  </template>
                </td>
              </tr>
            </template>
          </tbody>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import store from '@/store/store'
import translate from '@/utils/genTable/multilanguageHelper.js'
import RealTimeLabelSwitchComponent from './RealTimeLabelSwitchComponent'
import RealTimeVehicleStatusFilterComponent from './RealTimeVehicleStatusFilterComponent'
import RealTimeMapVehicleAndVehicleGroupsFilter from './RealTimeMapVehicleAndVehicleGroupsFilter'
import RealTimeSearchComponent from './RealTimeSearchComponent'
import RealTimeSelectAreaComponent from './RealTimeSelectAreaComponent'
import RealTimePoiFilterComponent from './RealTimePoiFilterComponent'
import mapMixin from '../mapMixin.js'
import formatDate from '@/utils/formatDateHelper.js'
import { useCaseMap } from '../../../utils/useCase/useCaseMap'

export default {
  name: 'RealTimeMapFiltersComponent',
  mixins: [mapMixin],
  components: {
    RealTimeLabelSwitchComponent,
    RealTimeVehicleStatusFilterComponent,
    RealTimeMapVehicleAndVehicleGroupsFilter,
    RealTimeSelectAreaComponent,
    RealTimePoiFilterComponent,
    RealTimeSearchComponent
  },
  data() {
    return {
      labels: {
        focusAll: translate('Focus all', 'mapFilters'),
        vehicles: translate('Vehicles', 'mapFilters'),
        engineBlockageActivation: translate("Engine blockage activation", "mapFilters"),
        engineBlockageDeactivation: translate("Engine blockage deactivation", "mapFilters")
      },
      options: {
        itemsPerPage: -1
      },
      poiView: false,
      vehicleForTable: [],
      showVehicleGroupFilter: false
    }
  },
  mounted() {
    let allowedUseCases = JSON.parse(store.getters['user/jwtData'].UserData).AllowedUseCaseIds
    this.showVehicleGroupFilter = allowedUseCases.includes(useCaseMap.SearchVehicleGroup)
  },
  beforeDestroy() {
    store.dispatch('filters/resetFilters')
  },
  computed: {
    ...mapState('map', ['vehicles', 'realTimeRoutes']),
    ...mapGetters('filters', ['getFilterFunction']),
    hasMultipleTenants() {
      const uniqueTenantIds = new Set(this.vehicles.filter(v => v.tenantId).map(v => v.tenantId))
      return uniqueTenantIds.size > 1
    },
    tableHeight: function() {
      if (this.options.itemsPerPage == -1) {
        return this.vehicles.length > 13 ? '50vh' : null
      }
      return null
    },
    headers() {
      let headers = [
        {
          text: '',
          value: 'focus',
          align: 'left',
          sortable: false,
          width: 30,
          class: 'map-table-header'
        },
        {
          text: translate('Vehicle', 'fields'),
          value: 'vehicleLabel',
          width: 120
        },
        { text: 'Km', value: 'todayKillometers', width: 70 },
        { text: 'Km/h', value: 'speed', width: 90 }
      ]
      let additionalColumns = this.tenant.readRealTimeMapAdditionalColumns

      if (additionalColumns.includes('Driver')) {
        headers.push({
          text: translate('Driver', 'fields'),
          value: 'driverName',
          width: 120
        })
      }

      if (additionalColumns.includes('PoiName')) {
        headers.push({ text: 'Poi', value: 'poi' })

        if (additionalColumns.includes('PoiEnteredAt')) {
          headers.push({
            text: translate('Poi time', 'fields'),
            value: 'poiEnteredAt',
            align: 'center',
            width: 180
          })
        }
      }

      if (additionalColumns.includes('VehicleLabel')) {
        headers.push({
          text: translate('Label', 'fields'),
          sortable : true,
          value : 'label',
          align: 'center',
          width : 200
        })
      }

      if(additionalColumns.includes('EngineBlockage')) {
        headers.push({
          text: translate('Engine blockage', 'fields'),
          align: 'center',
          value: 'engineBlockage',
          width: 150
        })
      }

      return headers
    },
  },
  watch: {
    poiView: {
      handler() {
        store.dispatch('filters/setVehicleStatus', [
          'Moving',
          'Turned off',
          'Stopped',
          'Inactive'
        ])
        store.dispatch('filters/setVehicleGroups', [])
        store.dispatch('filters/setSelectedPois', [])
        this.getFilterFunction.forEach(fnc => {
          fnc(this.vehicles, this.realTimeRoutes)
        })
        store.dispatch('map/clearPois')
      }
    },
    vehicles: {
      handler() {
        var that = this
        this.vehicleForTable = []
        this.vehicles.forEach(x => {
          if (x.shouldDisplay()) {
            that.vehicleForTable.push(x)
          }
        })
      },
      deep: true
    }
  },
  props: ['mobileBreakPoint'],
  methods: {
    convertDate(date) {
      if (!date) {
        return '/'
      }
      return formatDate(date.split('+')[0], 'long', true)
    },
    additionalColumnExist(key) {
      for (const item of this.headers) {
        if (item.value == key) return true
      }
    },
    getColor(vehicle) {
      if (vehicle.color == 1) {
        return 'row-green'
      } else if (vehicle.color == 2) {
        return 'row-blue'
      } else if(vehicle.color == 4) {
        return 'row-yellow'
      } else {
        return 'row-red'
      }
    },
    handleRowClick(item) {
      item.focused = !item.focused
      store.dispatch('map/zoomMap')
    },
    onFocusAll() {
      this.vehicles.forEach(vehicle => {
        vehicle.focused = false
      })
      store.dispatch('map/zoomToAllVisibleVehicles')
    },
    engineBlockage(item) {
      var engineBlockageRequest = {
        vehicleId : item.vehicleId,
        activate : !item.engineBlockageState
      }
      this.$formBuilderAxios.post("/vehicles/engineblockage", engineBlockageRequest).then(function() {
        item.engineBlockageButtonEnabled = false
        store.dispatch('map/updateVehicle', item)
        store.dispatch('notification/add', {type: 'success', message: "Uspešno izvršen zahtev." }, { root: true })
      }).catch((err) => {
        store.dispatch('notification/add', {type: 'error', message: err.response.data.Message}, { root: true })
      })
    }
  }
}
</script>

<style src="../../../assets/table_row.css" scoped>
.map-table-header {
  background-color: red;
}
</style>
